import { type StepUpMethod, type StepUpState, type UserEntity, UserType } from "@/shared/types/user.entity";

/**
 * Which step-up method the signed-in user answers a `x-two-factor-code` challenge with: the authenticator app
 * (`totp`), the security PIN (`pin`) or none yet. coloca-nest decides it (`step_up_method` on login and
 * `/auth/me`) — the authenticator wins whenever it is enrolled, the PIN counts only for organization members.
 * A backend that predates the PIN sends no field, and then the authenticator flag is the whole answer.
 *
 * Every step-up pre-check reads this, never `two_factor_enabled`: a member protected by a PIN has the flag
 * off and would otherwise be sent to enrol a method they already have.
 */
export function stepUpMethodOf(user: UserEntity | null | undefined): StepUpMethod | null {
  if (!user) return null;
  if (user.step_up_method !== undefined) return user.step_up_method;
  return user.two_factor_enabled ? "totp" : null;
}

/** True when a step-up action can open its challenge instead of the enrolment notice. */
export function hasStepUpMethod(user: UserEntity | null | undefined): boolean {
  return stepUpMethodOf(user) !== null;
}

/** The PIN is an organization member's option; INTERNAL staff stay on the authenticator. */
export function canUseSecurityPin(user: UserEntity | null | undefined): boolean {
  return user?.user_type === UserType.ORGANIZATION_MEMBER;
}

/**
 * What a challenge sends in `x-two-factor-code` to use the email backup: `email` asks the server to email a
 * one-time code for this very action, `email:NNNNNN` answers with it. The header — and so every service and
 * hook that already carries a step-up code — stays the same.
 */
export const STEP_UP_EMAIL_REQUEST = "email";

export function stepUpEmailCode(code: string): string {
  return `${STEP_UP_EMAIL_REQUEST}:${code}`;
}

/** The challenge may offer "send me a code by email": the backup is on and there is a method it backs up. */
export function canUseEmailBackup(user: UserEntity | null | undefined): boolean {
  return !!user && user.step_up_email_enabled === true && hasStepUpMethod(user);
}

/**
 * The email backup exists for this user at all: an organization member on a backend that reports it (it sends
 * `step_up_email_available`, false while there is no method yet). Settings and the method cards speak of it
 * only then, so an older backend never gets a switch it would refuse.
 */
export function supportsEmailBackup(user: UserEntity | null | undefined): boolean {
  return canUseSecurityPin(user) && typeof user?.step_up_email_available === "boolean";
}

/**
 * The step-up part of a user (login, `/auth/me`, the PIN and backup endpoints), so a re-read merges every
 * field the server decides — method, locks and the email backup — without naming them at each call site.
 */
export function pickStepUpState(user: StepUpState): StepUpState {
  return {
    step_up_method: user.step_up_method,
    security_pin_enabled: user.security_pin_enabled,
    step_up_locked_until: user.step_up_locked_until,
    security_pin_reset_required: user.security_pin_reset_required,
    step_up_email_enabled: user.step_up_email_enabled,
    step_up_email_available: user.step_up_email_available,
  };
}

export type StepUpLock = { kind: "temporary"; until: Date } | { kind: "reset" };

/**
 * The lock the signed-in user's step-up method is under right now, if any. Both methods take the temporary
 * one (five wrong codes in a row, `step_up_locked_until`); only the PIN has the hard lock that needs a new
 * PIN set with the password.
 */
export function stepUpLockOf(
  user: UserEntity | null | undefined,
  now: number = Date.now(),
): StepUpLock | null {
  const method = stepUpMethodOf(user);
  if (!user || !method) return null;
  if (method === "pin" && user.security_pin_reset_required) return { kind: "reset" };
  if (!user.step_up_locked_until) return null;
  const until = new Date(user.step_up_locked_until);
  return Number.isNaN(until.getTime()) || until.getTime() <= now ? null : { kind: "temporary", until };
}

/** "14:07" — what is left of a temporary step-up lock, rounded up to the second. */
export function formatLockRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
